/**
 * EditIdentityModal — inline corrector for candidate identity (typo fixer)
 * ET pour compléter les informations manquantes du dossier d'embauche
 * (date de naissance, nationalité, adresse, IBAN…) APRÈS que le contrat
 * ait été généré. Cf. brief CEO 2026-08-02 (mémo audio) :
 *
 *   "J'ai oublié de saisir des infos du candidat (adresse, date de
 *    naissance) *avant* de générer le contrat. Il faut me permettre de
 *    corriger a posteriori et de régénérer le PDF sans tout recommencer."
 *
 * Trigger : icône crayon sur la fiche candidat. Toutes les modifications
 * sont journalées côté serveur (`identity_history[]` pour la fiche
 * candidat, `admin_data_edits[]` pour le dossier d'embauche) — audit
 * URSSAF/DGCCRF.
 *
 * Le matricule `code_rd` reste IMMUTABLE dans tous les cas.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Pencil, Fingerprint, Loader2, FileText, RefreshCw } from "lucide-react";

import { api } from "../../lib/api";
import { issueContractCorrection } from "./ContractCorrectionPanel";
import { fieldErrors } from "../../lib/onboardingValidators";


const CIV_OPTIONS = [
    { value: "Monsieur", label: "Monsieur" },
    { value: "Madame", label: "Madame" },
];

const MARITAL_OPTIONS = [
    { value: "", label: "—" },
    { value: "celibataire", label: "Célibataire" },
    { value: "marie", label: "Marié·e" },
    { value: "pacse", label: "Pacsé·e" },
    { value: "divorce", label: "Divorcé·e" },
    { value: "veuf", label: "Veuf·ve" },
];


// Fields that live on the candidate document (fiche candidat).
const CAND_STRING_FIELDS = [
    "civilite", "first_name", "last_name",
    "prenom_usage", "nom_usage",
    "email", "phone", "city", "address",
];

// Fields that live on onboarding_forms.data.* (dossier d'embauche).
// Whitelist mirror of _ADMIN_EDITABLE_FORM_FIELDS on the backend.
const FORM_STRING_FIELDS = [
    "birth_date", "birth_city", "birth_dept", "birth_country",
    "nationality", "social_security_number",
    "address_line1", "address_postal_code",
    "marital_status", "iban", "bic",
];


export default function EditIdentityModal({ candidate, onClose, onSaved }) {
    // Candidate-level fields (patched via /candidates/{id})
    const [candForm, setCandForm] = useState(() => ({
        civilite: candidate.civilite || "",
        first_name: candidate.first_name || "",
        last_name: candidate.last_name || "",
        prenom_usage: candidate.prenom_usage || "",
        nom_usage: candidate.nom_usage || "",
        email: candidate.email || "",
        phone: candidate.phone || "",
        city: candidate.city || "",
        address: candidate.address || "",
    }));

    // Onboarding-form fields (patched via /onboarding/candidates/{id}/admin-data)
    const [formForm, setFormForm] = useState({
        civilite: "",
        birth_date: "",
        nationality: "",
        social_security_number: "",
        birth_city: "",
        birth_dept: "",
        birth_country: "",
        address_line1: "",
        address_postal_code: "",
        address_city: "",
        marital_status: "",
        iban: "",
        bic: "",
    });
    const [formLoaded, setFormLoaded] = useState(false);
    const [hasForm, setHasForm] = useState(false);
    const [formLocked, setFormLocked] = useState(false);
    const [saving, setSaving] = useState(false);
    // Snapshot of the loaded form values — used to compute the diff at submit
    // time. Populated ONCE after the initial fetch resolves.
    const initialFormRef = useRef(null);

    // Load the onboarding form data (silently — if 404, hide the section).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/onboarding/candidates/${candidate.id}`);
                if (cancelled) return;
                const d = res.data?.data || {};
                const hydrated = {
                    civilite: d.civilite || "",
                    birth_date: d.birth_date || "",
                    nationality: d.nationality || "",
                    social_security_number: d.social_security_number || "",
                    birth_city: d.birth_city || "",
                    birth_dept: d.birth_dept || "",
                    birth_country: d.birth_country || "",
                    address_line1: d.address_line1 || "",
                    address_postal_code: d.address_postal_code || "",
                    address_city: d.address_city || "",
                    marital_status: d.marital_status || "",
                    iban: d.iban || "",
                    bic: d.bic || "",
                };
                setFormForm(hydrated);
                initialFormRef.current = { ...hydrated };
                setHasForm(true);
                const st = res.data?.status;
                setFormLocked(st === "submitted" || st === "approved");
            } catch (e) {
                // 404 — no onboarding form yet. The admin can still bootstrap
                // one by uploading a doc; here we just hide the section.
                setHasForm(false);
                initialFormRef.current = { ...formForm };
            } finally {
                if (!cancelled) setFormLoaded(true);
            }
        })();
        return () => { cancelled = true; };
         
    }, [candidate.id]);

    function setCand(field, value) {
        setCandForm((f) => ({ ...f, [field]: value }));
    }
    function setForm(field, value) {
        setFormForm((f) => ({ ...f, [field]: value }));
    }

    // Diff for the candidate PATCH — only fields that actually changed.
    function buildCandDiff() {
        const diff = {};
        for (const f of CAND_STRING_FIELDS) {
            const before = (candidate[f] || "").trim();
            const after = (candForm[f] || "").trim();
            if (before !== after) {
                diff[f] = after === "" ? null : after;
            }
        }
        return diff;
    }

    async function handleSubmit(e) {
        e.preventDefault();

        // Enforce non-empty first_name + last_name (required downstream —
        // reports, contracts, PayFit).
        if ((candForm.first_name || "").trim() === "" || (candForm.last_name || "").trim() === "") {
            toast.error("Le prénom et le nom ne peuvent pas être vides");
            return;
        }

        const candDiff = buildCandDiff();
        const formDiff = initialFormRef.current
            ? _computeFormDiff(initialFormRef.current, formForm)
            : {};

        if (Object.keys(candDiff).length === 0 && Object.keys(formDiff).length === 0) {
            toast.info("Toutes les infos sont déjà à jour ✅", {
                description: "Aucune valeur n'a changé depuis la dernière sauvegarde. Rien à écrire côté serveur.",
            });
            return;
        }
        // CEO 2026-11-01 · Chaque info au bon endroit (ex. pas d'email dans l'adresse).
        const bad = Object.values(fieldErrors(formDiff));
        if (bad.length) {
            toast.error("Certaines informations ne sont pas au bon format", { description: bad.join(" · "), duration: 10000 });
            return;
        }

        try {
            setSaving(true);
            let candChanged = 0;
            let formChanged = 0;

            if (Object.keys(candDiff).length > 0) {
                await api.patch(`/candidates/${candidate.id}`, candDiff);
                candChanged = Object.keys(candDiff).length;
            }

            if (Object.keys(formDiff).length > 0) {
                const resForm = await api.patch(
                    `/onboarding/candidates/${candidate.id}/admin-data`,
                    formDiff,
                );
                formChanged = resForm?.data?.changed || Object.keys(formDiff).length;
            }

            // CEO 2026-10-23 · Propage la correction au contrat courant
            // (régénération + renvoi si non signé ; version corrigée si signé).
            let corr = { action: "none" };
            const contractFieldChanged = formChanged > 0 || ["civilite", "first_name", "last_name"].some((k) => k in candDiff);
            if (contractFieldChanged) {
                try {
                    ({ data: corr } = await api.post(`/contracts/candidates/${candidate.id}/apply-corrections`));
                } catch (_) { /* pas de droit contrat / pas de contrat : la fiche reste à jour */ }
            }

            // Iter80 · Broadcast so ContractCard (and any other listener)
            // knows to reload its own formData snapshot + PDF preview.
            // The parent CandidateProfile also gets notified via onSaved()
            // but ContractCard's local state is decoupled.
            try {
                window.dispatchEvent(new CustomEvent("prerec:candidate-data-updated", {
                    detail: {
                        candidate_id: candidate.id,
                        candidate_fields_changed: candChanged,
                        form_fields_changed: formChanged,
                    },
                }));
            } catch (_) { /* IE fallback not needed */ }

            const parts = [];
            if (candChanged) parts.push(`${candChanged} champ(s) fiche`);
            if (formChanged) parts.push(`${formChanged} champ(s) dossier d'embauche`);
            const matriculeNote = candidate.code_rd
                ? ` Matricule ${candidate.code_rd} conservé.`
                : "";
            const CONTRACT_NOTES = {
                regenerated: "Contrat (brouillon) régénéré avec les nouvelles informations.",
                regenerated_and_resent: "Contrat régénéré et renvoyé à la salariée (version corrigée).",
                correction_updated: "Version corrigée mise à jour et renvoyée pour signature.",
                signed: "Contrat déjà signé : émettez une version corrigée.",
                none: "Les prochains contrats (renouvellement inclus) utiliseront ces informations.",
            };
            const contractNote = CONTRACT_NOTES[corr.action] || CONTRACT_NOTES.none;
            toast.success("Informations mises à jour", {
                description: corr.action === "signed" ? contractNote : `${parts.join(" · ")}.${matriculeNote} ${contractNote}`,
                duration: corr.action === "signed" ? 20000 : 8000,
                action: corr.action === "signed" ? {
                    label: "Émettre une version corrigée",
                    onClick: async () => {
                        if (await issueContractCorrection(corr.contract_id)) {
                            window.dispatchEvent(new CustomEvent("prerec:candidate-data-updated", { detail: { candidate_id: candidate.id } }));
                        }
                    },
                } : undefined,
            });
            onSaved?.();
            onClose?.();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Erreur lors de la mise à jour";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="edit-identity-modal-backdrop"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-shariti-gray max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="edit-identity-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-shariti-gray shrink-0">
                    <h2 className="text-lg font-black text-shariti-black inline-flex items-center gap-2">
                        <Pencil className="h-4 w-4 text-shariti-orange" />
                        Corriger les informations du collaborateur
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="edit-identity-close"
                        className="text-slate-400 hover:text-shariti-black"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Reassurance banner — the CEO's #1 worry ("sans que cela ne puisse
                    remettre en cause tout le système d'attribution de code"). */}
                {candidate.code_rd && (
                    <div
                        className="mx-6 mt-4 px-3 py-2 rounded-md bg-shariti-orange-soft border border-shariti-orange/40 text-xs text-shariti-black flex items-start gap-2 shrink-0"
                        data-testid="edit-identity-code-preserve-banner"
                    >
                        <Fingerprint className="h-4 w-4 text-shariti-orange shrink-0 mt-0.5" />
                        <div>
                            <div className="font-bold">Matricule {candidate.code_rd} conservé</div>
                            <div className="text-slate-600">
                                Corriger l&apos;identité ou compléter le dossier d&apos;embauche n&apos;affecte JAMAIS
                                le code RD, ni les liens vers Booking / Reporting / PayFit.
                            </div>
                        </div>
                    </div>
                )}

                {/* Form (scrollable body) */}
                <form
                    onSubmit={handleSubmit}
                    className="flex-1 overflow-y-auto"
                    data-testid="edit-identity-form"
                >
                    <div className="p-6 space-y-5">
                        {/* ─── Section : Identité (fiche candidat) ─── */}
                        <SectionTitle icon={<Pencil className="h-3.5 w-3.5" />}>
                            Identité (fiche candidat)
                        </SectionTitle>

                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Civilité" span={1}>
                                <select
                                    value={candForm.civilite}
                                    onChange={(e) => setCand("civilite", e.target.value)}
                                    data-testid="edit-identity-civilite"
                                    className="input-cls"
                                >
                                    <option value="">—</option>
                                    {CIV_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Prénom" span={2}>
                                <input
                                    type="text"
                                    value={candForm.first_name}
                                    onChange={(e) => setCand("first_name", e.target.value)}
                                    data-testid="edit-identity-first-name"
                                    required
                                    className="input-cls"
                                />
                            </Field>
                        </div>

                        <Field label="Nom">
                            <input
                                type="text"
                                value={candForm.last_name}
                                onChange={(e) => setCand("last_name", e.target.value)}
                                data-testid="edit-identity-last-name"
                                required
                                className="input-cls"
                            />
                        </Field>

                        {/* iter69 — nom d'usage terrain / Evergiving */}
                        <div className="rounded-lg border border-dashed border-shariti-orange/40 bg-shariti-orange-soft/30 p-3">
                            <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-2">
                                Nom d&apos;usage terrain (optionnel)
                            </div>
                            <p className="text-[11px] text-slate-600 mb-3 leading-snug">
                                À remplir uniquement si connu différemment sur le terrain
                                (Evergiving, plannings). Les contrats/PayFit restent sur le
                                nom légal ci-dessus.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Prénom d'usage">
                                    <input
                                        type="text"
                                        value={candForm.prenom_usage}
                                        onChange={(e) => setCand("prenom_usage", e.target.value)}
                                        data-testid="edit-identity-prenom-usage"
                                        placeholder="Ex : Dimitri"
                                        className="input-cls"
                                    />
                                </Field>
                                <Field label="Nom d'usage">
                                    <input
                                        type="text"
                                        value={candForm.nom_usage}
                                        onChange={(e) => setCand("nom_usage", e.target.value)}
                                        data-testid="edit-identity-nom-usage"
                                        placeholder="Ex : Penard"
                                        className="input-cls"
                                    />
                                </Field>
                            </div>
                        </div>

                        {/* ─── Section : Contact ─── */}
                        <SectionTitle icon={<Pencil className="h-3.5 w-3.5" />}>
                            Contact
                        </SectionTitle>

                        <Field label="Email">
                            <input
                                type="email"
                                value={candForm.email}
                                onChange={(e) => setCand("email", e.target.value)}
                                data-testid="edit-identity-email"
                                className="input-cls"
                            />
                        </Field>

                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Téléphone">
                                <input
                                    type="tel"
                                    value={candForm.phone}
                                    onChange={(e) => setCand("phone", e.target.value)}
                                    data-testid="edit-identity-phone"
                                    className="input-cls"
                                />
                            </Field>
                            <Field label="Ville (fiche)">
                                <input
                                    type="text"
                                    value={candForm.city}
                                    onChange={(e) => setCand("city", e.target.value)}
                                    data-testid="edit-identity-city"
                                    className="input-cls"
                                />
                            </Field>
                        </div>

                        <Field label="Adresse (fiche)">
                            <input
                                type="text"
                                value={candForm.address}
                                onChange={(e) => setCand("address", e.target.value)}
                                data-testid="edit-identity-address"
                                placeholder="12 rue de la Paix, 75002 Paris"
                                className="input-cls"
                            />
                        </Field>

                        {/* ─── Section : Dossier d'embauche (contract) ─── */}
                        {formLoaded && (
                            <>
                                <SectionTitle icon={<FileText className="h-3.5 w-3.5" />}>
                                    Dossier d&apos;embauche (utilisé pour l&apos;Article 1 du contrat)
                                    {formLocked && (
                                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                            Dossier verrouillé · override admin
                                        </span>
                                    )}
                                </SectionTitle>

                                {!hasForm && (
                                    <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-[11px] text-sky-900" data-testid="edit-identity-form-bootstrap-hint">
                                        Aucun dossier d&apos;embauche n&apos;existe encore — remplir un champ
                                        ci-dessous créera automatiquement le dossier (silencieux, aucun
                                        email envoyé au candidat).
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Date de naissance">
                                        <input
                                            type="date"
                                            value={formForm.birth_date}
                                            onChange={(e) => setForm("birth_date", e.target.value)}
                                            data-testid="edit-identity-birth-date"
                                            className="input-cls"
                                        />
                                    </Field>
                                    <Field label="Nationalité">
                                        <input
                                            type="text"
                                            value={formForm.nationality}
                                            onChange={(e) => setForm("nationality", e.target.value)}
                                            data-testid="edit-identity-nationality"
                                            placeholder="Française"
                                            className="input-cls"
                                        />
                                    </Field>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Ville de naissance">
                                        <input
                                            type="text"
                                            value={formForm.birth_city}
                                            onChange={(e) => setForm("birth_city", e.target.value)}
                                            data-testid="edit-identity-birth-city"
                                            className="input-cls"
                                        />
                                    </Field>
                                    <Field label="Dépt naissance">
                                        <input
                                            type="text"
                                            value={formForm.birth_dept}
                                            onChange={(e) => setForm("birth_dept", e.target.value)}
                                            data-testid="edit-identity-birth-dept"
                                            placeholder="75"
                                            className="input-cls"
                                        />
                                    </Field>
                                    <Field label="Pays de naissance">
                                        <input
                                            type="text"
                                            value={formForm.birth_country}
                                            onChange={(e) => setForm("birth_country", e.target.value)}
                                            data-testid="edit-identity-birth-country"
                                            placeholder="France"
                                            className="input-cls"
                                        />
                                    </Field>
                                </div>

                                <Field label="Adresse complète (n° + rue)">
                                    <input
                                        type="text"
                                        value={formForm.address_line1}
                                        onChange={(e) => setForm("address_line1", e.target.value)}
                                        data-testid="edit-identity-address-line1"
                                        placeholder="12 rue de la Paix"
                                        className="input-cls"
                                    />
                                </Field>

                                <div className="grid grid-cols-3 gap-3">
                                    <Field label="Code postal">
                                        <input
                                            type="text"
                                            value={formForm.address_postal_code}
                                            onChange={(e) => setForm("address_postal_code", e.target.value)}
                                            data-testid="edit-identity-address-postal-code"
                                            placeholder="75002"
                                            className="input-cls"
                                        />
                                    </Field>
                                    <Field label="Ville" span={2}>
                                        <input
                                            type="text"
                                            value={formForm.address_city}
                                            onChange={(e) => setForm("address_city", e.target.value)}
                                            data-testid="edit-identity-address-city"
                                            placeholder="Paris"
                                            className="input-cls"
                                        />
                                    </Field>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="N° sécurité sociale">
                                        <input
                                            type="text"
                                            value={formForm.social_security_number}
                                            onChange={(e) => setForm("social_security_number", e.target.value)}
                                            data-testid="edit-identity-ssn"
                                            placeholder="1 85 12 75 123 456 78"
                                            className="input-cls"
                                        />
                                    </Field>
                                    <Field label="Situation familiale">
                                        <select
                                            value={formForm.marital_status}
                                            onChange={(e) => setForm("marital_status", e.target.value)}
                                            data-testid="edit-identity-marital"
                                            className="input-cls"
                                        >
                                            {MARITAL_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="IBAN" span={2}>
                                        <input
                                            type="text"
                                            value={formForm.iban}
                                            onChange={(e) => setForm("iban", e.target.value)}
                                            data-testid="edit-identity-iban"
                                            placeholder="FR76 3000 1007 9412 3456 7890 123"
                                            className="input-cls font-mono text-xs"
                                        />
                                    </Field>
                                </div>

                                <Field label="BIC">
                                    <input
                                        type="text"
                                        value={formForm.bic}
                                        onChange={(e) => setForm("bic", e.target.value)}
                                        data-testid="edit-identity-bic"
                                        placeholder="BDFEFRPPCCT"
                                        className="input-cls font-mono text-xs"
                                    />
                                </Field>
                            </>
                        )}
                    </div>
                </form>

                {/* Footer — regen reminder + actions */}
                <div className="border-t border-shariti-gray shrink-0">
                    <div className="mx-6 mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] leading-snug text-emerald-900 flex items-start gap-2" data-testid="edit-identity-regen-hint">
                        <RefreshCw className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                            <strong>À l&apos;enregistrement</strong>, le contrat est mis à jour
                            automatiquement : s&apos;il a été envoyé mais pas encore signé, la version
                            corrigée est renvoyée à la salariée (l&apos;ancien lien affiche la nouvelle version).
                        </span>
                    </div>
                    <div className="px-6 py-4 flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            data-testid="edit-identity-cancel"
                            className="px-4 py-2 rounded-md text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            data-testid="edit-identity-submit"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-black bg-shariti-orange text-white hover:bg-shariti-orange-dark disabled:opacity-40"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                            Enregistrer les corrections
                        </button>
                    </div>
                </div>
            </div>

            {/* Tailwind mini-class for inputs (kept local to avoid touching
                the global stylesheet) */}
            <style>{`
                .input-cls {
                    margin-top: 4px;
                    width: 100%;
                    border: 1px solid rgb(226 232 240);
                    border-radius: 0.375rem;
                    background: white;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.875rem;
                }
                .input-cls:focus {
                    border-color: rgb(255 106 0);
                    outline: none;
                }
            `}</style>
        </div>
    );
}


function SectionTitle({ icon, children }) {
    return (
        <div className="flex items-center gap-2 pt-2 pb-1 border-b border-slate-100">
            <span className="text-shariti-orange">{icon}</span>
            <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                {children}
            </h3>
        </div>
    );
}


function Field({ label, children, span }) {
    const cls = span === 2 ? "block col-span-2" : span === 1 ? "block col-span-1" : "block";
    return (
        <label className={cls}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
            {children}
        </label>
    );
}


// Compute the diff between two flat objects — only include keys whose
// (trimmed) value has changed. Used to keep the PATCH payload minimal.
function _computeFormDiff(initial, current) {
    const diff = {};
    const keys = new Set([...Object.keys(initial), ...Object.keys(current)]);
    for (const k of keys) {
        const before = (initial[k] || "").trim();
        const after = (current[k] || "").trim();
        if (before !== after) {
            diff[k] = after;
        }
    }
    return diff;
}
